import { TooltipProvider } from "@stylingcv/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Outlet } from "react-router-dom";

import { ArabicFontProvider } from "../components/arabic-font-provider";
import { DocumentLocalizedMeta } from "../components/document-localized-meta";
import { SkipNavigation } from "../components/skip-navigation";
import { SubscriptionTierSync } from "../components/subscription-tier-sync";
import { helmetContext } from "../constants/helmet";
import { OfflineIndicator, OfflineProvider } from "../contexts/offline-context";
import { ResumeLimitProvider } from "../contexts/resume-limit-context";
import { TokenModalProvider } from "../contexts/token-modal-context";
import { usePageTracking } from "../hooks/use-page-tracking";
import { queryClient } from "../libs/query-client";
import { AuthRefreshProvider } from "./auth-refresh";
import { DialogProvider } from "./dialog";
import { LocaleProvider } from "./locale";
import { SessionProvider } from "./session";
import { ThemeProvider } from "./theme";
import { Toaster } from "./toaster";

// Check if we're in development mode
const isDev = process.env.NODE_ENV === "development";

// Lazy-load dev-only component to keep it out of the production bundle
const DebugLogger = isDev ? lazy(() => import("../components/debug-logger")) : null;

export const Providers = () => {
  // Initialize page tracking
  usePageTracking();

  return (
    <LocaleProvider>
      <HelmetProvider context={helmetContext}>
        <DocumentLocalizedMeta />
        <QueryClientProvider client={queryClient}>
          <AuthRefreshProvider>
            <SessionProvider>
              <OfflineProvider>
                <TokenModalProvider>
                  <ResumeLimitProvider>
                    <ThemeProvider>
                      <TooltipProvider>
                        <DialogProvider>
                          <ArabicFontProvider>
                            <SubscriptionTierSync />
                            {/* Skip navigation link for keyboard users */}
                            <SkipNavigation />
                            <Outlet />
                          </ArabicFontProvider>

                          <Toaster />
                          <OfflineIndicator />

                          {/* Debug logger lazy-loaded only in development mode */}
                          {DebugLogger && (
                            <Suspense fallback={null}>
                              <DebugLogger />
                            </Suspense>
                          )}
                        </DialogProvider>
                      </TooltipProvider>
                    </ThemeProvider>
                  </ResumeLimitProvider>
                </TokenModalProvider>
              </OfflineProvider>
            </SessionProvider>
          </AuthRefreshProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </LocaleProvider>
  );
};
